import {
  IncidentCaseV2OpenRequestSchema,
  type IncidentCaseV2OpenResponse,
  type IncidentCaseV2Step,
  type IncidentCaseV2StepSaveResponse
} from '@lmc/validations'
import { postJSON } from 'common/services/api'
import { ApiError } from 'common/services/apiError'
import { incidentReportByLogQueryKey } from 'hooks/useIncidentReport/useIncidentReportByLog'
import { useMutation, useQueryClient } from 'react-query'
import type { IncidentCaseV2StepValues } from './incidentCaseV2Steps'

type SaveStepVariables = {
  /** Whether the case exists yet; when it does not, this save opens it. */
  isCaseOpen: boolean
  step: IncidentCaseV2Step
  values: IncidentCaseV2StepValues
}

const caseUrl = (incidentReportId: string) =>
  `/v6/incident-reports/${incidentReportId}/case`

/**
 * Saves one step of a v2 case.
 *
 * The first save opens the case (POST) from step 1's values; every later one
 * writes its step (PATCH). Both resolve to the steps the case has completed.
 * The report is refetched afterwards, so going Back shows what was saved.
 */
const useSaveIncidentCaseV2Step = ({
  incidentLogId,
  incidentReportId
}: {
  incidentLogId: string
  incidentReportId: string
}) => {
  const queryClient = useQueryClient()

  const { mutateAsync, isLoading } = useMutation(
    async ({
      isCaseOpen,
      step,
      values
    }: SaveStepVariables): Promise<
      IncidentCaseV2OpenResponse | IncidentCaseV2StepSaveResponse
    > => {
      const patch = () =>
        postJSON({
          url: caseUrl(incidentReportId),
          method: 'PATCH',
          json: { step, values }
        })

      if (isCaseOpen) {
        return (await patch()).data
      }

      try {
        // Opening needs step 1's required fields, which the step has already
        // validated; parsing narrows the step's optional values.
        const response = await postJSON({
          url: caseUrl(incidentReportId),
          json: IncidentCaseV2OpenRequestSchema.parse(values)
        })
        return response.data
      } catch (error) {
        // 409: already open (another tab, or an earlier open that failed after
        // its write), so save the step to it instead.
        if (error instanceof ApiError && error.status === 409) {
          return (await patch()).data
        }
        throw error
      }
    },
    {
      onSuccess: () =>
        queryClient.invalidateQueries(
          incidentReportByLogQueryKey(incidentLogId)
        )
    }
  )

  return { saveStep: mutateAsync, isSaving: isLoading }
}

export default useSaveIncidentCaseV2Step
